import React, { useEffect, useMemo } from 'react';
import { View, TouchableOpacity } from 'react-native'

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Icon from '../../../../components/IcomoonIcon';
import Input from '../../../../components/Forms/Input';
import { useForm } from 'react-hook-form';
import IconButton from '../../../../components/IconButton';

import useFormSubmit from '../../../../hooks/useFormSubmit';
import BottomSheet from '../../../../components/BottomSheet';
import useActiveAdminCommunity from '../../../../hooks/useActiveAdminCommunity';


interface BankAccount {
  uuid?: string;
  name: string;
  bank: string;
  account_number: string;
  clabe: string;
  owner_name: string;
}

interface BankAccountModalProps {
  visible: boolean;
  bankAccount: BankAccount | null;
  onRequestClose: () => void;
  onSaved: () => void;
}


export default function BankAccountModal({ visible, bankAccount, onRequestClose, onSaved }: BankAccountModalProps) {
  const { communityId } = useActiveAdminCommunity()
  const isEditing = !!bankAccount?.uuid;

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      name: '',
      bank: '',
      account_number: '',
      clabe: '',
      owner_name: '',
    }
  });


  useEffect(() => {
    if (bankAccount) {
      reset({
        name: bankAccount.name || '',
        bank: bankAccount.bank || '',
        account_number: bankAccount.account_number || '',
        clabe: bankAccount.clabe || '',
        owner_name: bankAccount.owner_name || '',
      });
    } else {
      reset({
        name: '',
        bank: '',
        account_number: '',
        clabe: '',
        owner_name: '',
      });
    }
  }, [bankAccount]);

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: onSaved,
    onError: (error) => { },
    successToastMessage: isEditing ? "Cuenta actualizada" : "Cuenta agregada",
    endpoint: isEditing
      ? `/communities/${communityId}/bank-accounts/${bankAccount?.uuid}`
      : `/communities/${communityId}/bank-accounts`,
    method: isEditing ? 'PUT' : 'POST',
  })

  return (
    <BottomSheet
      height={580}
      visible={visible}
      onRequestClose={onRequestClose}
    >
      <View style={tw`px-5`}>
        <View style={tw`row justify-between mb-4 mt-5`}>
          <Text style={tw`text-lg font-600 text-secondary`}>
            {isEditing ? 'Editar cuenta' : 'Agregar cuenta'}
          </Text>

          <TouchableOpacity style={tw`items-center justify-center mr-2`} onPress={onRequestClose}>
            <Icon name='close' style={tw`text-red`} size={26} />
          </TouchableOpacity>
        </View>

        <View style={tw`mb-1`}>
          <Text style={tw`text-gray-500 mb-1`}>Nombre</Text>
          <Input
            control={control}
            name='name'
            placeholder='Ej. Fondo de reserva'
            style={{ subcontainer: tw`rounded-2xl bg-gray-100` }}
            rules={{ required: true }}
          />
        </View>

        <View style={tw`mb-1`}>
          <Text style={tw`text-gray-500 mb-1`}>Banco</Text>
          <Input
            control={control}
            name='bank'
            placeholder='Nombre del banco'
            style={{ subcontainer: tw`rounded-2xl bg-gray-100` }}
            rules={{ required: true }}
          />
        </View>

        <View style={tw`mb-1`}>
          <Text style={tw`text-gray-500 mb-1`}>Cuenta</Text>
          <Input
            control={control}
            name='account_number'
            placeholder='Cuenta bancaria'
            style={{ subcontainer: tw`rounded-2xl bg-gray-100` }}
            keyboardType='numeric'
          />
        </View>

        <View style={tw`mb-1`}>
          <Text style={tw`text-gray-500 mb-1`}>CLABE</Text>
          <Input
            control={control}
            name='clabe'
            placeholder='18 dígitos'
            style={{ subcontainer: tw`rounded-2xl bg-gray-100` }}
            keyboardType='numeric'
            maxLength={18}
          />
        </View>

        <View style={tw`mb-4`}>
          <Text style={tw`text-gray-500 mb-1`}>Titular</Text>
          <Input
            control={control}
            name='owner_name'
            placeholder='Como aparece en la cuenta'
            style={{ subcontainer: tw`rounded-2xl bg-gray-100` }}
          />
        </View>

        <IconButton
          icon='check'
          title={isSubmitting ? "Guardando..." : "Guardar cuenta"}
          disabled={isSubmitting}
          style={tw`w-full py-3`}
          onPress={handleSubmit(submitForm)}
        />
      </View>
    </BottomSheet>
  );
}
